import { 
  Globe, 
  Search, 
  Megaphone, 
  TrendingUp, 
  Bot, 
  Code, 
  Palette, 
  Target 
} from 'lucide-react';
import { LucideIcon } from 'lucide-react';

export interface Service {
  id: number;
  slug: string;
  icon: LucideIcon;
  title: string;
  description: string;
  detailedDescription: string;
  features: string[];
  process: string[];
  deliverables: string[];
  image?: string; // Optional hero image for the service page
}

export const services: Service[] = [
  {
    id: 1,
    slug: 'web-development',
    icon: Globe,
    title: "Web Development",
    description: "We build fast, modern, and fully responsive websites engineered for performance — from landing pages to full-scale web applications.",
    detailedDescription: "Our web development team crafts pixel-perfect, high-performance websites using the latest frameworks and technologies. We focus on speed, accessibility, and SEO-ready architecture to ensure your site not only looks stunning but also performs flawlessly across all devices.",
    features: [
      "Custom React / Next.js applications",
      "Responsive & mobile-first design",
      "Lightning-fast load times (< 2s)",
      "CMS integration (WordPress, Strapi, Sanity)",
      "E-commerce solutions (Shopify, WooCommerce)",
      "Progressive Web Apps (PWA)",
    ],
    process: ["Discovery & Wireframing", "UI/UX Design", "Frontend & Backend Development", "Testing & QA", "Launch & Support"],
    deliverables: ["Fully responsive website", "Admin dashboard / CMS", "Performance audit report", "30-day post-launch support"],
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=2426",
  },
  {
    id: 2,
    slug: 'seo-optimization',
    icon: Search,
    title: "SEO Optimization",
    description: "Rank higher and get found. We implement technical SEO, on-page optimization, and content strategies that drive consistent organic traffic.",
    detailedDescription: "We take a data-driven approach to SEO — combining technical audits, keyword research, on-page optimization, and high-quality content strategies to drive sustainable organic growth. Our methods are white-hat and Google-approved.",
    features: [
      "Comprehensive technical SEO audit",
      "Keyword research & competitor analysis",
      "On-page & meta tag optimization",
      "Schema markup & structured data",
      "Link building & authority growth",
      "Monthly analytics & ranking reports",
    ],
    process: ["Site Audit & Analysis", "Keyword Strategy", "On-Page Optimization", "Content Planning", "Monitoring & Reporting"],
    deliverables: ["SEO audit report", "Keyword strategy document", "Monthly ranking reports", "Content calendar"],
    image: "https://elitedigitalagency.com/blog/wp-content/smush-webp/2022/06/iStock-997463524-1536x1014.jpg.webp",
  },
  {
    id: 3,
    slug: 'meta-ads-marketing',
    icon: Megaphone,
    title: "Meta Ads & Marketing",
    description: "Data-driven Meta ad campaigns designed to reach your ideal audience, maximize ROI, and scale your brand across Facebook and Instagram.",
    detailedDescription: "We create high-converting Meta (Facebook & Instagram) ad campaigns that precisely target your ideal customers. From creative strategy to audience segmentation and budget optimization, we maximize every dollar of your ad spend.",
    features: [
      "Custom audience creation & lookalike targeting",
      "A/B tested ad creatives & copy",
      "Retargeting & funnel-based campaigns",
      "Dynamic product ads for e-commerce",
      "Detailed conversion tracking & pixel setup",
      "Weekly performance optimization",
    ],
    process: ["Audience Research", "Creative Strategy", "Campaign Setup", "Launch & Monitor", "Optimize & Scale"],
    deliverables: ["Campaign strategy deck", "Ad creative assets", "Weekly performance reports", "ROI analysis dashboard"],
    image: "https://images.unsplash.com/photo-1533750349088-cd871a92f312?auto=format&fit=crop&q=80&w=2670",
  },
  {
    id: 4,
    slug: 'conversion-system',
    icon: TrendingUp,
    title: "Conversion System",
    description: "We build end-to-end lead generation systems — optimized funnels, landing pages, and follow-up flows that turn visitors into paying clients.",
    detailedDescription: "We engineer complete conversion ecosystems — from first click to final sale. Our systems include high-converting landing pages, automated email sequences, CRM integrations, and analytics dashboards that give you full visibility into your pipeline.",
    features: [
      "High-converting landing page design",
      "Multi-step sales funnels",
      "Email automation & drip sequences",
      "CRM setup & integration",
      "Lead scoring & qualification",
      "A/B testing & conversion rate optimization",
    ],
    process: ["Funnel Mapping", "Landing Page Design", "Automation Setup", "CRM Integration", "Testing & Optimization"],
    deliverables: ["Complete funnel system", "Automated email sequences", "CRM dashboard", "Conversion tracking setup"],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=2670",
  },
  {
    id: 5,
    slug: 'ai-automations',
    icon: Bot,
    title: "AI Automations",
    description: "Automate repetitive tasks and scale operations with custom AI agents and workflows that save time and reduce overhead.",
    detailedDescription: "We build intelligent automation systems powered by AI that handle repetitive operations, customer interactions, and data processing. From chatbots to workflow automations, we help businesses scale efficiently without scaling headcount.",
    features: [
      "Custom AI chatbots & virtual assistants",
      "Workflow automation (Zapier, Make, n8n)",
      "AI-powered data extraction & processing",
      "Automated customer support systems",
      "Intelligent document processing",
      "Custom GPT & LLM integrations",
    ],
    process: ["Process Analysis", "Solution Architecture", "AI Model Integration", "Automation Build", "Testing & Deployment"],
    deliverables: ["Deployed AI automation system", "Integration documentation", "Training materials", "Monitoring dashboard"],
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=2664",
  },
  {
    id: 6,
    slug: 'custom-software',
    icon: Code,
    title: "Custom Software",
    description: "Bespoke software solutions built from scratch — tailored to your exact business processes, integrations, and scale requirements.",
    detailedDescription: "We develop tailor-made software solutions that perfectly align with your business operations. Whether it's an internal tool, a SaaS platform, or a complex integration layer — we architect, build, and deploy software that grows with you.",
    features: [
      "Custom web & mobile applications",
      "API development & third-party integrations",
      "Database design & optimization",
      "Cloud infrastructure (AWS, GCP, Azure)",
      "Real-time dashboards & analytics",
      "Scalable microservice architecture",
    ],
    process: ["Requirements Gathering", "System Architecture", "Agile Development", "Quality Assurance", "Deployment & Maintenance"],
    deliverables: ["Production-ready software", "Technical documentation", "Source code & repository", "Ongoing maintenance plan"],
    image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&q=80&w=2670",
  },
  {
    id: 7,
    slug: 'poster-design',
    icon: Palette,
    title: "Poster Design",
    description: "Eye-catching poster and graphic design that communicates your message clearly — for social media, events, promotions, and print.",
    detailedDescription: "Our design team creates visually striking posters and graphics that capture attention and communicate your brand message instantly. We combine creative artistry with strategic thinking to produce designs that drive engagement and action.",
    features: [
      "Social media post & story designs",
      "Event & promotional posters",
      "Brand identity & logo design",
      "Infographic & data visualization",
      "Print-ready marketing collateral",
      "Animated graphics & motion design",
    ],
    process: ["Creative Brief", "Mood Board & Concepts", "Design Execution", "Feedback & Revisions", "Final Delivery"],
    deliverables: ["High-resolution design files", "Multiple format exports (PNG, PDF, SVG)", "Brand style guide", "Source files (Figma/PSD)"],
    image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&q=80&w=2671",
  },
  {
    id: 8,
    slug: 'personalized-digital-marketing',
    icon: Target,
    title: "Personalised Digital Marketing",
    description: "Tailored digital marketing strategies built around your specific audience, goals, and budget — not a one-size-fits-all approach.",
    detailedDescription: "We don't believe in cookie-cutter marketing. Our personalised strategies are built from the ground up based on your unique business goals, target audience behavior, and competitive landscape — ensuring every campaign delivers maximum impact.",
    features: [
      "Custom marketing strategy & roadmap",
      "Multi-channel campaign management",
      "Audience segmentation & persona development",
      "Content marketing & storytelling",
      "Performance analytics & attribution",
      "Brand positioning & messaging framework",
    ],
    process: ["Brand Discovery", "Market Research", "Strategy Development", "Campaign Execution", "Analysis & Refinement"],
    deliverables: ["Marketing strategy document", "Campaign playbooks", "Monthly performance reports", "Quarterly strategy reviews"],
    image: "https://images.unsplash.com/photo-1432821596592-e2c18b78144f?auto=format&fit=crop&q=80&w=2670",
  },
];
